import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import {
	AbstractControl,
	FormArray,
	FormBuilder,
	FormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { AddressService } from 'app/services/address.service';
import { ContactsService } from 'app/services/contacts.service';
import { UserService } from 'app/services/user.service';
import { DateTime } from 'luxon';
import { IAppointmentFormValue } from 'modules/appointments/interfaces/appointment';
import { UnleashService } from 'shared/unleash/unleash.service';

import { IRecurrentFrequencyOptions } from '../frequency-select/frequency-select.component';

@Component({
	selector: 'appointment-form',
	templateUrl: './appointment-form.component.html',
	styleUrls: ['./appointment-form.component.scss'],
})
export class AppointmentFormComponent implements OnInit, OnChanges {
	@Input() type: 'presencial' | 'online' | 'particular' = 'online';
	@Input() value: IAppointmentFormValue;
	@Input() action: 'create' | 'edit' = 'create';
	@Input() showAddDateButton = true;
	@Input() isReposition = false;
	@Input() invalidAppointmentNoShowSelected = false;
	@Input() isUserPremium;
	@Output() onChange = new EventEmitter();
	@Output() onFormInit = new EventEmitter();
	public appointmentForm: FormGroup;
	public isFormRead = false;
	public isContactLoaded = false;
	public minDate = this.getRoundedTime(DateTime.now()).toJSDate();
	public minEndDate = this.getRoundedTime(
		DateTime.now().plus({ minute: 30 })
	).toJSDate();
	public maxInitalDate = this.getRoundedTime(
		DateTime.now().set({ hour: 23, minute: 0 })
	).toJSDate();
	public maxEndDate = this.getRoundedTime(
		DateTime.now().set({ hour: 23, minute: 50 })
	).toJSDate();
	public locations = [];
	public userHasLocations = true;
	public isAddressLoaded = false;
	public currentContact: {
		data: {
			id: number;
			profileId?: string;
			name?: string;
		};
		preferences?: any;
		existsAsCustomer?: boolean;
	};
	public userPreferences: IUserPreferences;
	public frequencySelected: IRecurrentFrequencyOptions;
	public oldPrice: number;

	constructor(
		private fb: FormBuilder,
		private contacts: ContactsService,
		private addressService: AddressService,
		private userService: UserService,
		private router: Router,
		private unleashService: UnleashService,
		private actionTracker: ActionTrackerService
	) {}

	ngOnInit(): void {
		this.userPreferences = this.userService.getUserLocalPreferences();
		if (this.action === 'create') {
			this.actionTracker.eventTracker(
				SCOPE_TYPE.Appointment,
				SUBSCOPE_TYPE.Sidebar,
				ITEM_TYPE.Create,
				INTERACTION_TYPE.Click
			);
		}
		if (this.isReposition) {
			this.appointmentForm.patchValue({ recurrency: 'no-repeat' });
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.type?.currentValue === 'presencial') {
			if (this.locations.length === 0 && !this.isAddressLoaded) {
				this.getLocations();
			}
		}

		if (changes.isReposition?.currentValue !== this.value.isReposition) {
			this.value.isReposition = changes.isReposition?.currentValue;
		}

		if (changes.value || changes.type) {
			this.setFormModel();
		}
	}

	getRoundedTime(time) {
		return time.set({ minute: time.get('minute') - (time.get('minute') % 5) });
	}

	async setFormModel() {
		this.isFormRead = false;
		this.appointmentForm = this.fb.group({
			contact: [
				this.value.contact ?? '',
				this.type !== 'particular'
					? Validators.required
					: Validators.nullValidator,
			],
			contactExistsAsCustomer: this.currentContact?.existsAsCustomer ?? false,
			userId: [''],
			price: [
				this.value.price ?? 0,
				[
					this.type !== 'particular'
						? Validators.required
						: Validators.nullValidator,
					Validators.min(0),
					Validators.max(1000),
				],
			],
			duration: [
				50,
				this.type !== 'particular'
					? Validators.required
					: Validators.nullValidator,
			],
			professionalAddress: [
				this.value.professionalAddress ?? null,
				this.type === 'presencial'
					? Validators.required
					: Validators.nullValidator,
			],
			observations: [
				this.value?.observations || '',
				this.type === 'particular'
					? Validators.required
					: Validators.nullValidator,
			],
			recurrency: [this.value.recurrency],
			recurrencyMaxSessions: [],
			expiration: [null],
			frequencySelect: [
				this.frequencySelected ||
					this.value.frequencySelect ||
					IRecurrentFrequencyOptions.NoRepeat,
				this.type === 'particular'
					? Validators.required
					: Validators.nullValidator,
			],
			dates: this.fb.array(
				[
					this.fb.group({
						date: this.value.dates[0].date ?? new Date(),
						time:
							this.value.dates[0].time ??
							this.getRoundedTime(
								DateTime.now().plus({ minute: 30 })
							).toJSDate(),
					}),
				],
				this.type !== 'presencial'
					? Validators.required
					: Validators.nullValidator
			),
			particularDate: this.fb.group(
				{
					date: [
						this.value.dates[0].date ?? new Date(),
						this.type === 'particular'
							? Validators.required
							: Validators.nullValidator,
					],
					initialTime: [
						this.value.dates[0].time ??
							this.getRoundedTime(
								DateTime.now().plus({ minute: 30 })
							).toJSDate(),
						this.type === 'particular'
							? Validators.required
							: Validators.nullValidator,
					],
					finalTime: [
						this.value.dates[1]?.time
							? this.getRoundedTime(
									DateTime.fromJSDate(this.value.dates[1]?.time)
							  ).toJSDate()
							: this.getRoundedTime(
									DateTime.fromJSDate(
										this.value.dates[0]?.time || new Date()
									).plus({
										hour: 1,
									})
							  ).toJSDate(),
						this.type === 'particular'
							? Validators.required
							: Validators.nullValidator,
					],
				},
				this.type === 'particular'
					? Validators.required
					: Validators.nullValidator
			),
			isReposition: [
				this.value?.isReposition ?? false,
				this.type === 'online' ? Validators.required : Validators.nullValidator,
			],
			repositionOriginalEventId: [
				this.value?.repositionOriginalEventId,
				this.value.isReposition
					? Validators.required
					: Validators.nullValidator,
			],
		});
		this.appointmentForm.reset(this.appointmentForm.value);
		this.appointmentForm
			.get('expiration')
			.setValidators([
				this.createExpirationValidator(this.appointmentForm.value),
			]);
		this.onFormInit.emit(this.appointmentForm);
		this.isFormRead = true;
	}

	get dates(): FormArray {
		return this.appointmentForm.get('dates') as FormArray;
	}

	addDate() {
		this.dates.push(
			this.fb.group({
				date: !!this.dates.value[this.dates.length - 1].date
					? DateTime.fromJSDate(this.dates.value[this.dates.length - 1].date)
							.plus({ days: 7 })
							.toJSDate()
					: new Date(),
				time: !!this.dates.value[this.dates.length - 1].time
					? DateTime.fromJSDate(
							this.dates.value[this.dates.length - 1].time
					  ).toJSDate()
					: new Date(),
			})
		);
	}

	canShowRecurrencyOptions(): boolean {
		return (
			!this.appointmentForm.controls.isReposition.value &&
			this.type !== 'particular' &&
			this.action === 'create'
		);
	}

	onChangeDate(type, date, i?) {
		this.maxInitalDate = DateTime.fromJSDate(date).set({ hour: 23 }).toJSDate();
		this.maxEndDate = DateTime.fromJSDate(date)
			.set({ hour: 23, minute: 59 })
			.toJSDate();

		if (type === 'online' || type === 'presencial') {
			const timeValue = DateTime.fromJSDate(
				(this.appointmentForm.get('dates') as FormArray).value[i].time
			);
			this.appointmentForm
				.get('dates')
				.get(i.toString())
				.patchValue({
					time: DateTime.fromJSDate(date)
						.plus({
							hours: timeValue.get('hour'),
							minute: timeValue.get('minute'),
						})
						.toJSDate(),
				});
		} else if (type === 'particular') {
			const initialTime = DateTime.fromJSDate(
				this.appointmentForm.get('particularDate').value.initialTime
			);
			const finalTime = DateTime.fromJSDate(
				this.appointmentForm.get('particularDate').value.finalTime
			);
			this.appointmentForm.get('particularDate').patchValue({
				initialTime: DateTime.fromJSDate(date)
					.set({
						hour: initialTime.get('hour'),
						minute: initialTime.get('minute'),
					})
					.toJSDate(),
				finalTime: DateTime.fromJSDate(date)
					.set({
						hour: finalTime.get('hour'),
						minute: finalTime.get('minute'),
					})
					.toJSDate(),
			});
		}
		this.onChangeExpiration();
	}

	removeDate(event) {
		this.dates.removeAt(event);
	}

	getDateFormGroup(i) {
		return this.dates.get(i) as FormGroup;
	}

	onContactChanged(value) {
		this.isContactLoaded = false;
		if (value !== this.currentContact?.data?.id) {
			this.getContactPreferences(value);
		}
	}

	onChangeTime(time, type: 'initial' | 'final') {
		if (!time) {
			return;
		}

		const selectedDate = DateTime.fromJSDate(
			this.appointmentForm.get('particularDate').get('date').value
		);

		const _time = DateTime.fromJSDate(time);

		const freudInput = (
			document.querySelector(`#${type}Time .p-inputtext`) as HTMLInputElement
		)?.value;
		if (freudInput?.length === 5) {
			if (type === 'initial') {
				this.appointmentForm.get('particularDate').patchValue({
					finalTime: selectedDate
						.set({
							hour: _time.get('hour'),
							minute: _time.get('minute'),
						})
						.plus({ minute: 30 })
						.toJSDate(),
				});
			}
		}
		this.onChangeExpiration();
	}

	private async getContactPreferences(value) {
		if (!value) return;
		this.currentContact = {
			data: {
				id: value,
			},
		};
		this.contacts
			.getContact(value)
			.toPromise()
			.then((contact) => {
				this.currentContact = {
					data: {
						id: contact.id,
						profileId: contact.refUserId,
						name: contact.displayName,
					},
					preferences: {
						price: this.getPricePreference(contact),
						duration: 50,
					},
					existsAsCustomer: Boolean(contact.refUserId),
				};

				let price =
					this.appointmentForm.value.price === 0
						? this.currentContact.preferences.price
						: this.appointmentForm.value.price;
				if (this.appointmentForm.controls.isReposition.value) {
					this.oldPrice = price;
					price = 0;
				}

				this.appointmentForm.patchValue({
					userId: contact.refUserId,
					contactExistsAsCustomer: this.currentContact.existsAsCustomer,
					price,
				});
			});
	}

	getPricePreference(contact) {
		if (
			contact.sessionPriceHalfHour &&
			this.appointmentForm.value.duration === 30
		) {
			return contact.sessionPriceHalfHour;
		} else if (contact.sessionPrice) {
			return contact.sessionPrice;
		}
		return this.userPreferences.SessionValue;
	}

	getLocations() {
		this.addressService
			.getAddresses()
			.toPromise()
			.then((res) => {
				this.locations = res.map((location) => {
					return {
						value: location.id,
						label: location.name,
						location,
					};
				});
			})
			.catch((err) => {
				if (err.status === 404) {
					this.userHasLocations = false;
				}
			})
			.finally(() => {
				this.isAddressLoaded = true;
			});
	}

	goToAddressPage() {
		this.router.navigateByUrl('/account/address/new');
	}

	selectFrequencySelect(event: IRecurrentFrequencyOptions) {
		this.frequencySelected = event;
		this.appointmentForm.controls['frequencySelect'].setValue(event);
	}

	changeIsReposition(value: boolean): void {
		if (this.value.isReposition === value) return;
		this.value.isReposition = value;
		let newPrice: number = this.oldPrice;
		if (value) {
			this.oldPrice =
				this.oldPrice || this.appointmentForm.controls.price.value;
			newPrice = 0;
			this.keepOnlyFirstDate();
			this.appointmentForm.patchValue({ recurrency: 'no-repeat' });
		}
		this.value.price = newPrice;
		this.appointmentForm.patchValue({ price: this.value.price });
		this.appointmentForm.patchValue({ isReposition: this.value.isReposition });
		if (!value)
			this.appointmentForm.patchValue({ repositionOriginalEventId: null });
		this.appointmentForm.controls.repositionOriginalEventId.setValidators(
			this.value.isReposition ? Validators.required : Validators.nullValidator
		);
		this.appointmentForm.controls.repositionOriginalEventId.updateValueAndValidity();
	}

	keepOnlyFirstDate() {
		if (this.dates.length < 2) return;
		let index = this.dates.length - 1;
		do {
			this.removeDate(index);
			--index;
		} while (this.dates.length > 1);
	}

	selectAppointmentNoShow(appointmentId: string) {
		this.appointmentForm.patchValue({
			repositionOriginalEventId: appointmentId,
		});
	}

	onChangeExpiration() {
		this.appointmentForm
			.get('expiration')
			.setValidators([
				this.createExpirationValidator(this.appointmentForm.value),
			]);
		this.appointmentForm.get('expiration').updateValueAndValidity();
	}

	createExpirationValidator(formValue): ValidatorFn {
		const dates = formValue.dates;
		//Checa se a data de expiração é possivel na data atual
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			const now = DateTime.now();
			if (typeof value === 'number') {
				const hasSomeExpirationDateInvalid = dates.find((date) => {
					const appointmentLuxonDate = DateTime.fromJSDate(date.date).set({
						hour: DateTime.fromJSDate(date.time).get('hour'),
						minute: DateTime.fromJSDate(date.time).get('minute'),
					});
					const expirationTime: DateTime = appointmentLuxonDate.minus({
						minutes: value,
					});
					return expirationTime < now;
				});
				if (hasSomeExpirationDateInvalid) {
					return { expiration: true };
				}
			}
			if (value === null) {
				return null;
			}
		};
	}
}
